import React, { useEffect, useRef, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Box, Typography, Link } from '@mui/material';
import { C } from '../theme';
import { isWebglSupported } from './mapSupport';
import './map.css';

interface LocationMapProps {
  lat: number;
  lng: number;
  height?: number;
  zoom?: number;
}

const DARK_STYLE = 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json';
const GOLD = '#F3C015';

/** Read-only dark map with a single gold pin. Scroll-zoom is disabled so it
 *  never hijacks page scrolling; users zoom with the +/- buttons.
 *  Falls back to a coordinates card + external map link when WebGL is missing. */
const LocationMap: React.FC<LocationMapProps> = ({ lat, lng, height = 280, zoom = 14 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [supported] = useState(isWebglSupported);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!supported || !containerRef.current) return;
    let map: maplibregl.Map;
    try {
      map = new maplibregl.Map({
        container: containerRef.current,
        style: DARK_STYLE,
        center: [lng, lat],
        zoom,
        scrollZoom: false,
        dragRotate: false,
        attributionControl: { compact: true },
      });
    } catch (e) {
      setFailed(true);
      return;
    }
    map.on('error', () => {});
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-right');
    const marker = new maplibregl.Marker({ color: GOLD }).setLngLat([lng, lat]).addTo(map);
    return () => {
      marker.remove();
      map.remove();
    };
  }, [lat, lng, zoom, supported]);

  if (!supported || failed) {
    return (
      <Box
        sx={{
          height,
          borderRadius: 2,
          border: `1px solid ${C.line}`,
          backgroundColor: C.surface2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          textAlign: 'center',
          px: 3,
        }}
      >
        <Typography variant="body2" sx={{ color: C.muted }}>
          Koordinate: {lat.toFixed(5)}, {lng.toFixed(5)}
        </Typography>
        <Link
          href={`https://www.google.com/maps?q=${lat},${lng}`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: '#F6CB4B', fontWeight: 600 }}
        >
          Otvori u mapama
        </Link>
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      className="ni-map"
      sx={{ height, width: '100%', borderRadius: 2, overflow: 'hidden', border: `1px solid ${C.line}` }}
    />
  );
};

export default LocationMap;
